import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import update from 'immutability-helper'
import Select from 'react-select'
import numeral from 'numeral'

import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import * as ValidationRules from 'Pods/Shared/Rules/Rules'
import Icon from 'Pods/Shared/Icon/Icon'
import Tooltip from 'Pods/Shared/Tooltip'
import InputField from 'Pods/Shared/InputField'
import InputFieldWithPrefix from 'Pods/Shared/InputFieldWithPrefix'
import TextareaField from 'Pods/Shared/TextareaField'
import TagsInputField from 'Pods/Shared/TagsInputField'
import RichEditor from 'Pods/Shared/RichEditor/RichEditor'
import { sortAlphabetically } from 'Utils/sortAlphabetically'

import DetailsStepImages from './DetailsStepImages'
import DetailsStepEmbeds from './DetailsStepEmbeds'
import ImagesDropzone from './ImagesDropzone'

const fieldValidations = [
  RuleRunner(
    'title',
    'Pack Title',
    ValidationRules.required,
    ValidationRules.isAlphanumeric,
    ValidationRules.validateCompetitors,
    ValidationRules.minLength(5),
    ValidationRules.maxLength(64)
  ),
  RuleRunner('description', 'Pack Description', ValidationRules.validateCompetitors),
  RuleRunner('price', 'Price', ValidationRules.min(10), ValidationRules.max(10000)),
  RuleRunner('customLicense', 'Custom license', ValidationRules.required),
  RuleRunner('tags', 'tags', ValidationRules.minArrLength(5), ValidationRules.maxArrLength(20)),
]

class DetailsStep extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      title: props.data.title,
      description: props.data.description && props.data.description !== '<p></p>' ? props.data.description : '',
      tags: props.data.tags,
      price: props.data.price,
      license: props.data.license,
      customLicense: props.data.customLicense ? props.data.customLicense : '',
      images: props.images.map((image) => ({ id: Number(image.id), largeImageUrl: image.attributes.imageName.url })),
      imagesToUpload: [],
      embeds: props.embeds.map((embed) => ({
        id: embed.attributes.id,
        url: embed.attributes.image.gridUrl,
        title: embed.attributes.title,
        type: embed.attributes.type,
      })),
      removedImageIds: [],
      preloaders: [],
      showErrors: false,
      disableActions: false,
      disableDropZone: false,
      tagHasError: false,
      descriptionHasError: false,
    }

    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputBlur = this.handleInputBlur.bind(this)
    this.handleEditorChange = this.handleEditorChange.bind(this)
    this.errorFor = this.errorFor.bind(this)
    this.updateCollection = this.updateCollection.bind(this)
    this.onUpdateSuccess = this.onUpdateSuccess.bind(this)
    this.publishCollection = this.publishCollection.bind(this)
    this.onPublishSuccess = this.onPublishSuccess.bind(this)
    this.onPublishFailure = this.onPublishFailure.bind(this)
    this.onDeleteDraftClick = this.onDeleteDraftClick.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
    this.onTagsChange = this.onTagsChange.bind(this)
    this.onImagesChange = this.onImagesChange.bind(this)
    this.onEmbedsChange = this.onEmbedsChange.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.onUploadSuccess = this.onUploadSuccess.bind(this)
    this.onUploadFailure = this.onUploadFailure.bind(this)
    this.checkIfFormIsValid = this.checkIfFormIsValid.bind(this)
  }

  componentWillMount() {
    this.setState({ validationErrors: run(this.state, fieldValidations) })
  }

  handleInputChange(e) {
    const newState = update(this.state, { [e.target.id]: { $set: e.target.value } })
    newState.validationErrors = run(newState, fieldValidations)

    this.setState(newState)
  }

  handleInputBlur() {
    this.updateCollection()
  }

  handleEditorChange(value, hasError) {
    const newState = update(this.state, { description: { $set: value }, descriptionHasError: { $set: hasError } })
    newState.validationErrors = run(newState, fieldValidations)

    this.setState(newState)
  }

  errorFor(field) {
    return this.state.validationErrors[field] || ''
  }

  checkIfFormIsValid() {
    const errorsArr = Object.getOwnPropertyNames(this.state.validationErrors)
    const incorrectTags = this.state.tags.length < 5 || this.state.tagHasError || this.state.tags.length > 20
    const incorrectDescription = this.state.description.length < 5 || this.state.descriptionHasError
    const hasNoImages = this.state.images.length === 0
    const hasTooManyImages = this.state.images.length > 200

    if (incorrectTags || incorrectDescription || hasNoImages || hasTooManyImages) {
      return false
    }

    if (errorsArr.length !== 0) {
      if (this.props.license !== 'custom' && errorsArr.length === 1 && errorsArr.includes('customLicense')) {
        return true
      }
      return false
    }
    return true
  }

  handleFormSubmit(e) {
    e.preventDefault()

    this.setState({ showErrors: true })

    this.updateCollection(true, true)
  }

  updateCollection(publish = false, disableActions = false) {
    if (this.props.data.status === 'draft' || (this.props.data.status !== 'draft' && publish)) {
      if (this.checkIfFormIsValid() || !publish) {
        if (disableActions) {
          this.setState({ disableActions: true })
        }

        axios.patch(`/profile/items/${this.props.data.id}`, {
          item: {
            custom_license: this.state.customLicense,
            description: this.state.description,
            license: this.state.license,
            tags: this.state.tags,
            title: this.state.title,
            price: this.state.price,
            image_ids: this.state.images.map(image => image.id),
            embed_ids: this.state.embeds.map(embed => embed.id),
            removed_image_ids: this.state.removedImageIds.map(id => id),
          },
          dont_validate: !publish,
        })
          .then(() => this.onUpdateSuccess(publish))
          .catch(this.onPublishFailure)
      } else {
        if (publish) {
          App.events.publish('app:notification:flash:error', 'Not all details are entered, please take another look')

          if (this.state.images.length > 200) {
            App.events.publish('app:notification:flash:error', "Pack can't have more than 200 images")
          }
        }

        return null
      }
    } else {
      return null
    }
  }

  onUpdateSuccess(publish) {
    if (publish) {
      this.publishCollection()
    }
  }

  publishCollection() {
    axios.post(`/profile/items/${this.props.data.id}/publish`, {
      item: {
        tags: this.state.tags,
      },
    })
      .then(this.onPublishSuccess)
      .catch(this.onPublishFailure)
  }

  onPublishSuccess() {
    this.setState({ disableActions: false }, this.props.onModalClose)
    App.events.publish('app:notification:flash:success', `Pack was successfully ${this.props.data.status === 'draft' ? 'published' : 'updated'}`)
    setTimeout(() => window.location.reload(), 3000)
  }

  onPublishFailure() {
    this.setState({ disableActions: false })
    App.events.publish('app:notification:flash:error', 'Something went wrong, please try again')
  }

  onDeleteDraftClick() {
    this.setState({ disableActions: true })
  }

  onSortChange(data) {
    this.setState({ license: data.value })
    this.updateCollection()
  }

  onTagsChange(tags, hasError) {
    const newState = update(this.state, { tags: { $set: tags }, tagHasError: { $set: hasError } })
    newState.validationErrors = run(newState, fieldValidations)

    this.setState(newState)
    this.updateCollection()
  }

  onImagesChange(images, removedImageIds) {
    this.setState({ images, removedImageIds })
    this.updateCollection()
  }

  onEmbedsChange(embeds) {
    this.setState({ embeds })
    this.updateCollection()
  }

  onDrop(files) {
    const { images, preloaders } = this.state
    let imagesCount = images.length + preloaders.length
    let imagesErrorShown = false
    const uploadFilesCount = files.length

    files.forEach((file) => {
      if (imagesCount < 200) {
        imagesCount += 1

        const data = new FormData()
        data.append('item_file', file)
        data.append('type', 'image')

        this.setState(prevState => ({
          preloaders: prevState.preloaders.concat(true),
        }))

        axios.post(`/profile/items/${this.props.data.id}/uploads`, data)
          .then((r) => this.onUploadSuccess(r, uploadFilesCount))
          .catch((r) => this.onUploadFailure(r, uploadFilesCount))
      } else if (!imagesErrorShown) {
        App.events.publish('app:notification:flash:error', "Pack can't have more than 200 images")
        imagesErrorShown = true
      }
    })
  }

  onUploadSuccess(response, totalCount) {
    const { imagesToUpload, preloaders, images } = this.state
    const { data: { image } } = response

    if (imagesToUpload.length === totalCount - 1) {
      const sortedImages = [...imagesToUpload, image]
        .filter(Boolean) // remove errored images if any
        .sort((a, b) => sortAlphabetically(a, b, 'fileName')) // sort by fileName

      this.setState({
        imagesToUpload: [],
        images: update(images, { $push: sortedImages }),
        preloaders: preloaders.slice(0, -1),
      })
    } else {
      this.setState({
        imagesToUpload: update(imagesToUpload, { $push: [image] }),
        preloaders: preloaders.slice(0, -1),
      })
    }

    this.updateCollection()
  }

  onUploadFailure(err, totalCount) {
    const { imagesToUpload, preloaders, images } = this.state

    Sentry.captureException(err)

    if (imagesToUpload.length === totalCount - 1) {
      const sortedImages = [...imagesToUpload]
        .filter(Boolean) // remove errored images if any
        .sort((a, b) => sortAlphabetically(a, b, 'fileName')) // sort by fileName

      this.setState({
        imagesToUpload: [],
        images: update(images, { $push: sortedImages }),
        preloaders: preloaders.slice(0, -1),
      })
    } else {
      this.setState({
        imagesToUpload: update(imagesToUpload, { $push: null }),
        preloaders: preloaders.slice(0, -1),
      })
    }

    App.events.publish('app:notification:flash:error', 'Something went wrong with image upload')
  }

  render() {
    return (
      <div className="overlay-modal--details-content">
        <button className="btn btn-transparent overlay-modal--close-inner" onClick={this.props.onModalClose}>
          <Icon type={['fal', 'times']} color="grey-dark" />
        </button>

        <div className="content-wrapper">
          <div className="header-text">
            <div className="section__title">Pack details and information</div>
            <p className="text-default">
              Fill out the basic information about your pack. Use a descriptive title and include any relevant
              technical details in the description. Add at least 15-20 tags to ensure your pack is easily discoverable by customers.
            </p>
            <p className="text-default">
              <strong>Important:</strong> Please note that the maximum allowable pack size is 1TB.
              We highly recommend optimizing your packs accordingly.
              If you require assistance or have any questions, please contact our support team at <a href="mailto:support@cgtrader.com">support@cgtrader.com</a>
            </p>
          </div>

          <form onSubmit={this.handleFormSubmit}>
            <div className="section__details">
              <div className="modal-section__title">Details</div>
              <InputField
                label="Pack Title"
                id="title"
                name="title"
                required
                errorText={this.errorFor('title')}
                showError={this.state.showErrors}
                value={this.state.title}
                formClassName="form-group-grey"
                inputClassName="form-control-grey"
                onChange={this.handleInputChange}
                onBlur={this.handleInputBlur}
              />

              <RichEditor
                label="Pack Description"
                required
                value={this.state.description}
                onChange={this.handleEditorChange}
                minLength={5}
                showError={this.state.showErrors}
                errorText={this.errorFor('description')}
                onBlur={this.handleInputBlur}
              />

              <TagsInputField
                id="tags"
                label="Tags"
                required
                value={this.state.tags}
                onChange={this.onTagsChange}
                recommendedTags={this.props.data.recommendedTags}
                hideRecommendedTags={this.props.isEditing && this.state.tags.length >= 20}
                max={20}
                errorText={this.errorFor('tags')}
                showError={this.state.showErrors}
              />
            </div>

            <div className="section__pricing-and-license">
              <div className="modal-section__title">Pricing and license</div>
              <div className="row">
                <div className="col-6 col-sm-5 col-md-4 section__pricing">
                  <Tooltip
                    id="min-price"
                    className="u-block"
                    content="Minimal price should be $10"
                  >
                    <InputFieldWithPrefix
                      label="Price"
                      id="price"
                      name="price"
                      type="number"
                      prefix="$"
                      required
                      simpleError
                      errorText={this.errorFor('price')}
                      showError={this.state.showErrors}
                      value={this.state.price}
                      formClassName="form-group-grey"
                      inputClassName="form-control-grey"
                      onChange={this.handleInputChange}
                      onBlur={this.handleInputBlur}
                      customContent={this.props.suggestedPrice !== 0 ? `Individually, these items cost ${numeral(this.props.suggestedPrice).format('$0.[00]')}` : ''}
                    />
                  </Tooltip>

                  <div className="separator" />
                </div>

                <div className="col-6 col-sm-5 col-md-4">
                  <div className="form-group form-group-grey">
                    <label>License</label>
                    <Select
                      className="Select-outline"
                      id="license-select"
                      options={I18n.translate('publisher.licenses_paid')}
                      onChange={this.onSortChange}
                      value={this.state.license}
                      searchable={false}
                      clearable={false}
                    />
                  </div>
                </div>

                {(this.state.license === 'custom' || this.state.license === 'custom_no_ai')
                  && (
                  <div className="col-12">
                    <TextareaField
                      label={this.state.license === 'custom' ? 'Custom license' : 'Custom, no Ai'}
                      id="customLicense"
                      name="custom_license"
                      required
                      rows={5}
                      errorText={this.errorFor('customLicense')}
                      showError={this.state.showErrors}
                      value={this.state.customLicense}
                      formClassName="form-group-grey"
                      inputClassName="form-control-grey"
                      onChange={this.handleInputChange}
                      onBlur={this.handleInputBlur}
                    />
                  </div>
                  )}
              </div>

            </div>

            {this.state.images.length > 0 || this.state.preloaders.length > 0 ? (
              <DetailsStepImages
                key={this.state.images.length}
                itemId={this.props.data.id}
                images={this.state.images}
                removedImageIds={this.state.removedImageIds}
                onChange={this.onImagesChange}
                preloaders={this.state.preloaders}
                onDragStateChange={({ isActive }) => this.setState({ disableDropZone: isActive })}
              />
            ) : (
              <div className="text-warning u-mt10">Please upload at least one preview image.</div>
            )}

            <ImagesDropzone isDisabled={this.state.disableDropZone} onDrop={this.onDrop} />

            <DetailsStepEmbeds
              itemId={this.props.data.id}
              embeds={this.state.embeds}
              onChange={this.onEmbedsChange}
              onDragStateChange={({ isActive }) => this.setState({ disableDropZone: isActive })}
            />

            <div className="footer u-text-right u-mt10">
              <div className="content-wrapper">
                {this.props.data.status === 'draft' && (
                  <Fragment>
                    <a
                      href={`/profile/items/${this.props.data.id}`}
                      data-method="delete"
                      disabled={this.state.disableActions}
                      className={`btn btn-outline-default btn-fat ${this.state.disableActions ? 'is-disabled' : ''}`}
                      onClick={this.onDeleteDraftClick}
                    >
                      Delete draft
                    </a>
                    <span className="separator" />

                    <a
                      href={`/preview-3d-model/${this.props.data.id}`}
                      target="_blank"
                      className={`btn btn-outline-default btn-fat ${this.state.disableActions ? 'is-disabled' : ''}`}
                      rel="noreferrer"
                    >
                      Preview
                    </a>
                  </Fragment>
                )}
                <button type="submit" className="btn btn-fat btn-primary u-ml10" disabled={this.state.disableActions}>
                  {this.props.data.status === 'draft' ? 'Publish' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }
}

DetailsStep.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    price: PropTypes.number,
    license: PropTypes.string.isRequired,
    customLicense: PropTypes.string,
    recommendedTags: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.string.isRequired,
  }).isRequired,
  images: PropTypes.array.isRequired,
  isEditing: PropTypes.bool,
  suggestedPrice: PropTypes.number,
  notAddedItems: PropTypes.array,
}

DetailsStep.defaultProps = {
  data: {
    title: '',
    description: '',
    tags: [],
    price: 0,
    customLicense: '',
    recommendedTags: [],
  },
  isEditing: false,
  suggestedPrice: 0,
  notAddedItems: [],
}

export default DetailsStep
